import React, { useEffect, useState } from 'react';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Divider,
    Modal,
    Paper,
    Stack,
    Switch,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Icon } from '@iconify/react';
import {
    confirmationDialogSx,
    confirmationIconSx,
    confirmationPrimaryButtonSx,
    confirmationSecondaryButtonSx,
    confirmationTextSx,
    confirmationTitleSx,
} from '../../../styles/dialogStyles';

function formatUpdatedAt(value) {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleString();
}

/**
 * Reusable "live sending" switch card backed by a platform-wide toggle endpoint.
 * Both the Email and SMS configuration cards on the Communications screen render
 * this with different copy and service functions. The parent owns the shared
 * snackbar and receives updates via {@code onNotify}.
 */
function LiveSendingToggleCard({
    title,
    description,
    ariaLabel,
    confirmTitleOn,
    confirmTitleOff,
    confirmTextOn,
    confirmTextOff,
    successNoun,
    loadErrorText,
    saveErrorText,
    getConfig,
    updateConfig,
    onNotify,
    confirmTitleId,
}) {
    const [enabled, setEnabled] = useState(false);
    const [updatedAt, setUpdatedAt] = useState(null);
    const [updatedBy, setUpdatedBy] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [pendingValue, setPendingValue] = useState(false);

    const applyConfig = (config) => {
        if (config && typeof config.liveSendingEnabled === 'boolean') {
            setEnabled(config.liveSendingEnabled);
            setUpdatedAt(config.updatedAt || null);
            setUpdatedBy(config.updatedBy || null);
            return true;
        }
        return false;
    };

    useEffect(() => {
        let active = true;
        (async () => {
            const config = await getConfig();
            if (!active) return;
            if (!applyConfig(config)) {
                onNotify(loadErrorText, 'error');
            }
            setLoading(false);
        })();
        return () => {
            active = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const requestToggle = (event) => {
        // Defer the change until the admin confirms — guards against accidental flips.
        setPendingValue(event.target.checked);
        setConfirmOpen(true);
    };

    const cancelToggle = () => setConfirmOpen(false);

    const confirmToggle = async () => {
        const nextValue = pendingValue;
        setConfirmOpen(false);
        setSaving(true);

        const result = await updateConfig(nextValue);
        if (applyConfig(result)) {
            onNotify(`${successNoun} turned ${nextValue ? 'on' : 'off'}`, 'success');
        } else {
            // enabled was never changed, so the switch already reflects the real state.
            onNotify(saveErrorText, 'error');
        }
        setSaving(false);
    };

    const lastUpdated = formatUpdatedAt(updatedAt);

    return (
        <>
            <Paper
                elevation={0}
                sx={{
                    p: 4,
                    borderRadius: 4,
                    border: '1px solid #E7ECF2',
                    backgroundColor: '#FFFFFF',
                    maxWidth: 820,
                }}
            >
                <Typography sx={{ fontSize: 18, fontWeight: 600, color: '#202124', mb: 1 }}>
                    {title}
                </Typography>
                <Typography sx={{ color: '#6B7280', mb: 3 }}>{description}</Typography>

                <Divider sx={{ borderColor: '#EEF2F7', mb: 3 }} />

                {loading ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1 }}>
                        <CircularProgress size={20} />
                        <Typography sx={{ color: '#6B7280' }}>Loading current settings&hellip;</Typography>
                    </Box>
                ) : (
                    <>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <Switch
                                    checked={enabled}
                                    onChange={requestToggle}
                                    disabled={saving || confirmOpen}
                                    inputProps={{ 'aria-label': ariaLabel }}
                                />
                                <Chip
                                    label={enabled ? 'On' : 'Off'}
                                    size="small"
                                    sx={{
                                        fontWeight: 600,
                                        color: enabled ? '#1B5E20' : '#6B7280',
                                        backgroundColor: enabled ? '#E6F4EA' : '#F1F1F1',
                                    }}
                                />
                            </Box>
                            {saving && <CircularProgress size={18} />}
                        </Box>

                        {(lastUpdated || updatedBy) && (
                            <Typography sx={{ mt: 2.5, fontSize: 13, color: '#9AA0A6' }}>
                                Last updated
                                {lastUpdated ? ` on ${lastUpdated}` : ''}
                                {updatedBy ? ` by ${updatedBy}` : ''}.
                            </Typography>
                        )}
                    </>
                )}
            </Paper>

            <Modal open={confirmOpen} onClose={cancelToggle} aria-labelledby={confirmTitleId}>
                <Box sx={confirmationDialogSx}>
                    <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1 }}>
                        <Button
                            onClick={cancelToggle}
                            sx={{ minWidth: 'auto', p: 0.5, color: '#6B7280', borderRadius: '999px' }}
                        >
                            <CloseIcon />
                        </Button>
                    </Stack>
                    <Stack spacing={2} alignItems="center" textAlign="center">
                        <Box sx={confirmationIconSx}>
                            <Icon icon="mingcute:warning-line" width="36" height="36" color="#C40000" />
                        </Box>
                        <Typography id={confirmTitleId} sx={confirmationTitleSx}>
                            {pendingValue ? confirmTitleOn : confirmTitleOff}
                        </Typography>
                        <Typography sx={confirmationTextSx}>
                            {pendingValue ? confirmTextOn : confirmTextOff}
                        </Typography>
                        <Stack direction="row" spacing={1.5} justifyContent="center" sx={{ pt: 1 }}>
                            <Button variant="contained" onClick={confirmToggle} sx={confirmationPrimaryButtonSx}>
                                {pendingValue ? 'Yes, turn on' : 'Yes, turn off'}
                            </Button>
                            <Button onClick={cancelToggle} sx={confirmationSecondaryButtonSx}>
                                Cancel
                            </Button>
                        </Stack>
                    </Stack>
                </Box>
            </Modal>
        </>
    );
}

export default LiveSendingToggleCard;
